import { apiFetch } from "@/services/api-client";
import type {
  AccessRequest,
  AccessRequestApproveResult,
  AdminStats,
  AdminUserCreateInput,
  AdminUserUpdateInput,
  GroupSummary,
  PickupArea,
  PickupAreaCreateInput,
  PickupAreaUpdateInput,
  User,
} from "@/types/api";

export async function listAccessRequests(): Promise<AccessRequest[]> {
  return apiFetch<AccessRequest[]>("/admin/access-requests");
}

export async function approveAccessRequest(
  requestId: string,
): Promise<AccessRequestApproveResult> {
  return apiFetch<AccessRequestApproveResult>(`/admin/access-requests/${requestId}/approve`, {
    method: "POST",
  });
}

export async function rejectAccessRequest(requestId: string): Promise<void> {
  await apiFetch<{ message: string }>(`/admin/access-requests/${requestId}/reject`, {
    method: "POST",
  });
}

export async function listUsers(): Promise<User[]> {
  return apiFetch<User[]>("/admin/users");
}

export async function getStats(): Promise<AdminStats> {
  return apiFetch<AdminStats>("/admin/stats");
}

export async function createUser(input: AdminUserCreateInput): Promise<User> {
  return apiFetch<User>("/admin/users", { method: "POST", body: input });
}

export async function updateUser(userId: string, input: AdminUserUpdateInput): Promise<User> {
  return apiFetch<User>(`/admin/users/${userId}`, { method: "PATCH", body: input });
}

/** Immediate GDPR erasure of a deactivated account. */
export async function anonymiseUser(userId: string): Promise<void> {
  await apiFetch<{ message: string }>(`/admin/users/${userId}/anonymise`, { method: "POST" });
}

/** Every car, paused ones included (browse leaves those out). */
export async function listGroups(): Promise<GroupSummary[]> {
  return apiFetch<GroupSummary[]>("/admin/groups");
}

export async function resetPassword(userId: string, newPassword: string): Promise<void> {
  await apiFetch<{ message: string }>(`/admin/users/${userId}/reset-password`, {
    method: "POST",
    body: { new_password: newPassword },
  });
}

export async function listAllAreas(query = ""): Promise<PickupArea[]> {
  const params = query ? `?q=${encodeURIComponent(query)}` : "";
  return apiFetch<PickupArea[]>(`/admin/pickup-areas${params}`);
}

export async function createArea(input: PickupAreaCreateInput): Promise<PickupArea> {
  return apiFetch<PickupArea>("/admin/pickup-areas", { method: "POST", body: input });
}

export async function updateArea(
  areaId: string,
  input: PickupAreaUpdateInput,
): Promise<PickupArea> {
  return apiFetch<PickupArea>(`/admin/pickup-areas/${areaId}`, {
    method: "PATCH",
    body: input,
  });
}
